import { Button, TextInput } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import React from "react";
import { CXAutomationAccordion } from "../CXAutomationAccordion";
import { SectionDescription } from "../SectionDescription";
import { DataInputItem } from "../CXAutomationApiSetupSection/components/DataInputRow";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";

type Props = {
  isOpen: boolean;
  onOpenChange: (next: boolean) => void;
  dataInputs: DataInputItem[];
  tokenValues: Record<string, string>;
  onTokenValuesChange: (next: Record<string, string>) => void;
  onTest: () => void;
  isTestDisabled: boolean;
  isTestRunning: boolean;
  testResponse: string | null;
  onSave: () => void;
  isSaveDisabled: boolean;
  isSaving: boolean;
};

export const CXAutomationTestResponseSection: React.FC<Props> = ({
  isOpen,
  onOpenChange,
  dataInputs,
  tokenValues,
  onTokenValuesChange,
  onTest,
  isTestDisabled,
  isTestRunning,
  testResponse,
  onSave,
  isSaveDisabled,
  isSaving,
}) => {
  const { t } = useTranslations();

  const namedInputs = dataInputs.filter((input) => input.name.trim());

  const handleTokenValueChange = (name: string, value: string) => {
    onTokenValuesChange({ ...tokenValues, [name]: value });
  };

  return (
    <CXAutomationAccordion
      number={3}
      title={t({
        id: "cx-actions-page.test-response",
        defaultMessage: "Test Response",
      })}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-8">
          {namedInputs.length > 0 && (
            <div className="flex flex-col gap-4">
              <SectionDescription
                title={t({
                  id: "cx-actions-page.token-values",
                  defaultMessage: "Token Values",
                })}
              />
              {namedInputs.map((input) => (
                <TextInput
                  key={input.id}
                  labelPosition="border-overlap"
                  label={input.name}
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  value={tokenValues[input.name] ?? ""}
                  onChange={(e) =>
                    handleTokenValueChange(
                      input.name,
                      e.target.value.trimStart()
                    )
                  }
                />
              ))}
            </div>
          )}

          <div
            className={cx(
              "flex h-[260px] max-h-[260px] min-h-[260px] flex-col overflow-hidden rounded-[10px] border border-solid border-grey-400 font-secondary text-base leading-4"
            )}
          >
            {testResponse ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-4 font-secondary text-sm leading-4 break-words whitespace-pre-wrap text-dark">
                {testResponse}
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-4 text-sm leading-[21px] text-[#808080]">
                {t({
                  id: "cx-actions-page.test-response-live-placeholder",
                  defaultMessage:
                    "Click on “Test response” to load response from your API",
                })}
              </div>
            )}
            <div className="flex shrink-0 px-5 py-4">
              <Button
                iconEnd={<ChevronIcon />}
                variant="tertiary"
                onClick={onTest}
                isDisabled={isTestDisabled}
                isLoading={isTestRunning}
              >
                {t({
                  id: "cx-actions-page.test-response-action",
                  defaultMessage: "Test response",
                })}
              </Button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button
            className="w-[120px]"
            variant="secondary"
            isDisabled={isSaveDisabled}
            isLoading={isSaving}
            onClick={onSave}
          >
            {t({
              id: "common.save",
              defaultMessage: "Save",
            })}
          </Button>
        </div>
      </div>
    </CXAutomationAccordion>
  );
};
